import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    Offcanvas, 
    OffcanvasHeader, 
    OffcanvasBody, 
    Button, 
    FormGroup, 
    Label, 
    Input,
    Row,
    Col
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/it';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarPlus,
    faCheck,
    faTimes,
    faEnvelope,
    faMobileAlt,
    faUserGroup,
    faBell,
    faTruck
} from '@fortawesome/free-solid-svg-icons';

import EmployeeSelect from '../EmployeeSelect';
import CustomerSelect from '../CustomerSelect';
import { getNodeApiUrl } from '../../config/api.config';
import { selectAccountData } from '../../redux/main/accountSlice';

moment.locale('it');

const GenericAppointmentEditOffcanvas = ({ 
    isOpen, 
    onClose, 
    appointment,
    onSuccess 
}) => {
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const accountId = accountData?._id;
    
    // Stati form
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startTime, setStartTime] = useState('08:00');
    const [endTime, setEndTime] = useState('17:00');
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedSystem, setSelectedSystem] = useState(null);
    const [customerSystems, setCustomerSystems] = useState([]);
    const [selectedSupplierId, setSelectedSupplierId] = useState('');
    const [selectedSupplierLabel, setSelectedSupplierLabel] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    // Stati notifiche
    const [sendEmployeeEmail, setSendEmployeeEmail] = useState(false);
    const [sendEmployeePush, setSendEmployeePush] = useState(false);
    const [sendCustomerEmail, setSendCustomerEmail] = useState(false);
    const [sendSupplierEmail, setSendSupplierEmail] = useState(false);
    
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Carica fornitori
    useEffect(() => {
        const loadSuppliers = async () => {
            if (!token || !accountId) return;
            
            setLoadingSuppliers(true);
            try {
                const response = await axios.get(
                    getNodeApiUrl(`suppliers/suppliersList?accountId=${accountId}`),
                    { headers: { 'auth-token': token } }
                );
                if (response.data && Array.isArray(response.data)) {
                    setSuppliers(response.data);
                }
            } catch (error) {
                console.error('Errore caricamento fornitori:', error);
            } finally {
                setLoadingSuppliers(false);
            }
        };
        
        if (isOpen) {
            loadSuppliers();
        }
    }, [isOpen, token, accountId]);

    // Popola form quando si apre con i dati dell'appuntamento
    useEffect(() => {
        if (isOpen && appointment) {
            setTitle(appointment.title || '');
            setDescription(appointment.notes || '');
            
            if (appointment.fromDate) {
                setStartDate(moment(appointment.fromDate).format('YYYY-MM-DD'));
                setStartTime(moment(appointment.fromDate).format('HH:mm'));
            }
            if (appointment.toDate) {
                setEndDate(moment(appointment.toDate).format('YYYY-MM-DD'));
                setEndTime(moment(appointment.toDate).format('HH:mm'));
            }
            
            // Collaboratori
            if (appointment.users && appointment.users.length > 0) {
                setSelectedEmployees(appointment.users.map(u => ({
                    value: u.userId,
                    label: u.userLabel,
                    data: { color: u.userColor }
                })));
            } else {
                setSelectedEmployees([]);
            }
            
            // Cliente
            if (appointment.customerId) {
                setSelectedCustomer({
                    value: appointment.customerId,
                    label: appointment.customerLabel
                });
            } else {
                setSelectedCustomer(null);
            }
            
            // Sistema/Impianto
            if (appointment.systemId) {
                setSelectedSystem({
                    _id: appointment.systemId,
                    label: appointment.systemLabel,
                    title: appointment.systemLabel,
                    customerAddressId: appointment.systemAddressId,
                    customerAddressLabel: appointment.systemAddressLabel,
                    geoData: appointment.geoData
                });
            } else {
                setSelectedSystem(null);
            }
            
            // Fornitore
            if (appointment.supplierId) {
                setSelectedSupplierId(appointment.supplierId);
                setSelectedSupplierLabel(appointment.supplierLabel || '');
            } else {
                setSelectedSupplierId('');
                setSelectedSupplierLabel('');
            }
            
            // Reset notifiche
            setSendEmployeeEmail(false);
            setSendEmployeePush(false);
            setSendCustomerEmail(false);
            setSendSupplierEmail(false);
        }
    }, [isOpen, appointment]);

    // Gestione selezione cliente
    const handleCustomerChange = (selected) => {
        setSelectedCustomer(selected);
        setSelectedSystem(null);
        setCustomerSystems([]);
    };

    // Carica gli impianti quando viene selezionato un cliente
    useEffect(() => {
        const fetchSystems = async () => {
            if (!selectedCustomer?.value || !accountId) {
                setCustomerSystems([]);
                setSelectedSystem(null);
                return;
            }
            try {
                const authToken = localStorage.getItem('installamelo-auth-token');
                const response = await axios.get(
                    getNodeApiUrl(`systems/systemsList?accountId=${accountId}&customerId=${selectedCustomer.value}`),
                    { headers: { 'auth-token': authToken } }
                );
                if (Array.isArray(response.data)) {
                    setCustomerSystems(response.data);
                } else if (response.data?.data) {
                    setCustomerSystems(response.data.data);
                } else {
                    setCustomerSystems([]);
                }
            } catch (error) {
                console.error('Errore caricamento impianti:', error);
                setCustomerSystems([]);
            }
        };
        fetchSystems();
    }, [selectedCustomer, accountId]);

    // Gestione selezione fornitore
    const handleSupplierChange = (e) => {
        const supplierId = e.target.value;
        setSelectedSupplierId(supplierId);
        
        const supplier = suppliers.find(s => s._id === supplierId);
        setSelectedSupplierLabel(supplier ? (supplier.company || supplier.code || '') : '');
    };

    // Salva modifiche
    const handleSave = async () => {
        // Validazioni
        if (!startDate || !endDate) {
            toast.warning('Seleziona le date');
            return;
        }
        
        if (startDate > endDate) {
            toast.warning('La data di inizio deve essere precedente o uguale alla data di fine');
            return;
        }
        
        if (startDate === endDate && startTime >= endTime) {
            toast.warning('L\'orario di inizio deve essere precedente all\'orario di fine');
            return;
        }

        setIsSubmitting(true);
        
        try {
            // Leggi il token fresco al momento della chiamata
            const authToken = localStorage.getItem('installamelo-auth-token');
            if (!authToken) {
                toast.error('Sessione scaduta. Effettua nuovamente il login.');
                setIsSubmitting(false);
                return;
            }
            
            const fromDate = moment(`${startDate} ${startTime}`, 'YYYY-MM-DD HH:mm');
            const toDate = moment(`${endDate} ${endTime}`, 'YYYY-MM-DD HH:mm');
            const duration = toDate.diff(fromDate, 'minutes');
            const timezoneDiff = new Date().getTimezoneOffset();

            const updateData = {
                accountId,
                appointment: {
                    _id: appointment._id,
                    fromDate: fromDate.toISOString(),
                    toDate: toDate.toISOString(),
                    duration: duration,
                    timezoneDiff: timezoneDiff,
                    users: selectedEmployees.map(emp => ({
                        userId: emp.value,
                        userLabel: emp.label,
                        userColor: emp.data?.color || '#7367f0'
                    })),
                    vehicles: [],
                    equipments: [],
                    notes: description,
                    title: title || 'Appuntamento Generico',
                    type: 1,
                    isGenericAppointment: true,
                    active: true,
                    customerId: selectedCustomer?.value || null,
                    customerLabel: selectedCustomer?.label || null,
                    systemId: selectedSystem?._id || null,
                    systemLabel: selectedSystem?.label || selectedSystem?.title || null,
                    systemAddressId: selectedSystem?.customerAddressId || null,
                    systemAddressLabel: selectedSystem?.customerAddressLabel || null,
                    supplierId: selectedSupplierId || null,
                    supplierLabel: selectedSupplierLabel || null,
                    geoData: selectedSystem?.geoData || {
                        type: 'Point',
                        coordinates: [0, 0],
                        distanceKm: 0,
                        distanceTime: 0
                    },
                    notifications: {
                        sendEmployeeEmail,
                        sendEmployeePush,
                        sendCustomerEmail,
                        sendSupplierEmail
                    }
                }
            };
            
            const response = await axios.patch(
                getNodeApiUrl('appointments'),
                updateData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': authToken
                    }
                }
            );
            
            if (response.data && (response.data._id || response.status === 200)) {
                toast.success('Appuntamento aggiornato con successo!');
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                toast.error('Errore nell\'aggiornamento dell\'appuntamento');
            }
        } catch (error) {
            console.error('Errore nell\'aggiornamento dell\'appuntamento:', error);
            toast.error('Errore nell\'aggiornamento: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Offcanvas
            isOpen={isOpen}
            toggle={onClose}
            direction="end"
            style={{ width: '35%', minWidth: '450px', zIndex: 200001 }}
        >
            <OffcanvasHeader toggle={onClose}>
                <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
                Modifica Appuntamento Generico
            </OffcanvasHeader>
            <OffcanvasBody>
                <div className="alert alert-warning py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                    <FontAwesomeIcon icon={faCalendarPlus} className="me-2" />
                    Gli appuntamenti generici <strong>NON</strong> permettono di caricare ore
                </div>

                {/* Titolo */}
                <FormGroup>
                    <Label>Titolo</Label>
                    <Input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Es: Riunione, Sopralluogo, ecc..."
                    />
                </FormGroup>
                
                {/* Date */}
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Data Da <span className="text-danger">*</span></Label>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => {
                                    const newDate = e.target.value;
                                    setStartDate(newDate);
                                    if (newDate > endDate) {
                                        setEndDate(newDate);
                                    }
                                }}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Data A <span className="text-danger">*</span></Label>
                            <Input
                                type="date"
                                value={endDate}
                                min={startDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                
                {/* Orari */}
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Ora Inizio</Label>
                            <Input
                                type="time"
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Ora Fine</Label>
                            <Input
                                type="time"
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                
                {/* Collaboratori */}
                <FormGroup>
                    <Label>Collaboratori</Label>
                    <EmployeeSelect
                        isMulti
                        selectedOptions={selectedEmployees}
                        setSelectedOptions={setSelectedEmployees}
                        placeholder="Seleziona collaboratori..."
                    />
                </FormGroup>
                
                {/* Cliente */}
                <FormGroup>
                    <Label>Cliente (opzionale)</Label>
                    <CustomerSelect
                        value={selectedCustomer}
                        onChange={handleCustomerChange}
                        placeholder="Seleziona cliente..."
                        isClearable
                    />
                </FormGroup>
                
                {/* Impianto - visibile solo se c'è un cliente selezionato */}
                {selectedCustomer && (
                    <FormGroup>
                        <Label>Impianto</Label>
                        {customerSystems.length > 0 ? (
                            <Input
                                type="select"
                                value={selectedSystem?._id || ''}
                                onChange={(e) => {
                                    const system = customerSystems.find(s => s._id === e.target.value);
                                    setSelectedSystem(system || null);
                                }}
                            >
                                <option value="">Seleziona impianto...</option>
                                {customerSystems.map(system => (
                                    <option key={system._id} value={system._id}>
                                        {system.title} - {system.customerAddressLabel || 'Indirizzo non specificato'}
                                    </option>
                                ))}
                            </Input>
                        ) : (
                            <div className="text-muted small">Nessun impianto disponibile per questo cliente</div>
                        )}
                    </FormGroup>
                )}
                
                {/* Fornitore - temporaneamente nascosto
                <FormGroup>
                    <Label>
                        <FontAwesomeIcon icon={faTruck} className="me-1" />
                        Fornitore (opzionale)
                    </Label>
                    <Input
                        type="select"
                        value={selectedSupplierId}
                        onChange={handleSupplierChange}
                        disabled={loadingSuppliers}
                    >
                        <option value="">
                            {loadingSuppliers ? 'Caricamento...' : 'Seleziona fornitore...'}
                        </option>
                        {suppliers.map(supplier => (
                            <option key={supplier._id} value={supplier._id}>
                                {supplier.company || supplier.code || 'Fornitore'}
                            </option>
                        ))}
                    </Input>
                </FormGroup>
                */}
                
                {/* Descrizione */}
                <FormGroup>
                    <Label>Descrizione</Label>
                    <Input
                        type="textarea"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Note o descrizione dell'appuntamento..."
                    />
                </FormGroup>
                
                {/* Sezione Notifiche */}
                {(selectedEmployees.length > 0 || selectedCustomer || selectedSupplierId) && (
                    <>
                        <h6 className="mb-3 mt-4">Notifiche</h6>
                        
                        {/* Notifica Cliente */}
                        {selectedCustomer && (
                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                        <strong>Email al Cliente</strong>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={sendCustomerEmail}
                                            onChange={(e) => setSendCustomerEmail(e.target.checked)}
                                        />
                                    </div>
                                </div>
                                <small className="text-muted">
                                    Cliente: {selectedCustomer?.label || 'N/D'}
                                </small>
                            </div>
                        )}
                        
                        {/* Notifica Fornitore */}
                        {selectedSupplierId && (
                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-warning" />
                                        <strong>Email al Fornitore</strong>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={sendSupplierEmail}
                                            onChange={(e) => setSendSupplierEmail(e.target.checked)}
                                        />
                                    </div>
                                </div>
                                <small className="text-muted">
                                    Fornitore: {selectedSupplierLabel || 'N/D'}
                                </small>
                            </div>
                        )}
                        
                        {/* Notifiche Collaboratori */}
                        {selectedEmployees.length > 0 && (
                            <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                <h6 className="mb-3">
                                    <FontAwesomeIcon icon={faUserGroup} className="me-2 text-info" />
                                    Notifiche ai Collaboratori
                                </h6>
                                
                                <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faMobileAlt} className="me-2 text-success" />
                                        <span>Notifica Push</span>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={sendEmployeePush}
                                            onChange={(e) => setSendEmployeePush(e.target.checked)}
                                        />
                                    </div>
                                </div>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
                                        <span>Email</span>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            role="switch"
                                            checked={sendEmployeeEmail}
                                            onChange={(e) => setSendEmployeeEmail(e.target.checked)}
                                        />
                                    </div>
                                </div>
                                <small className="text-muted d-block mt-2">
                                    Collaboratori: {selectedEmployees.map(e => e.label).join(', ')}
                                </small>
                            </div>
                        )}
                    </>
                )}
                
                {/* Pulsanti */}
                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                    <Button color="secondary" outline onClick={onClose} disabled={isSubmitting}>
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        Annulla
                    </Button>
                    <Button color="success" onClick={handleSave} disabled={isSubmitting}>
                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                        {isSubmitting ? 'Salvataggio...' : 'Salva Modifiche'}
                    </Button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default GenericAppointmentEditOffcanvas;
